import { Navigate, RouteObject, useParams } from 'react-router-dom';
import { CoreTypography } from '@youscience/core';
import { QueryClient } from '@tanstack/react-query';
import { userViewLoaderType } from '@features/users/interfaces/usersLoaderTypes';
import ErrorPage from '../error-page';
import { USERS_ROUTES } from '.';
import { CommonBreadcrumbs } from './commonBreadcrumbs';

// Redirect legacy /users/edit/:userId to the card-aware /users/:userId page.
const LegacyUserEditRedirect = () => {
  const { userId } = useParams<{ userId: string }>();

  return <Navigate to={`/${USERS_ROUTES.USERS}/${userId ?? ''}`} replace />;
};
// Lazy components
const queryClient = new QueryClient();

export const usersRoutes: RouteObject[] = [
  {
    path: USERS_ROUTES.USERS,
    errorElement: <ErrorPage />,
    children: [
      {
        async lazy() {
          const { UsersTable } = await import('@features/users/UsersTable/UsersTable');

          return { Component: UsersTable };
        },
        errorElement: <ErrorPage />,
        index: true,
        async loader() {
          const { usersTableLoader } = await import('@features/users/loaders');

          return usersTableLoader(queryClient);
        },
        handle: {
          crumb: () => [<CommonBreadcrumbs.Home />, <CoreTypography color='text.secondary'>Users</CoreTypography>],
        },
      },
      {
        async lazy() {
          const { UserView } = await import('@features/users/UserView/UserView');

          return { Component: UserView };
        },
        errorElement: <ErrorPage />,
        async loader({ request, params }) {
          const { userViewLoader } = await import('@features/users/loaders');

          return userViewLoader({ request, params });
        },
        path: USERS_ROUTES.DETAILS,
        handle: {
          crumb: (user: userViewLoaderType) => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Users />,

            <CoreTypography color='text.secondary'>{user?.details?.data?.fullName ?? 'Details'}</CoreTypography>,
          ],
        },
      },
      {
        // Legacy edit URL — keep mounted so stale bookmarks land on the new view.
        Component: LegacyUserEditRedirect,
        errorElement: <ErrorPage />,
        path: USERS_ROUTES.EDIT_USER,
      },
      {
        async lazy() {
          const { UsersBulkInvite } = await import('@features/invitations/UsersBulkInvite');

          return { Component: UsersBulkInvite };
        },
        errorElement: <ErrorPage />,
        path: USERS_ROUTES.BULK_INVITE,
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Users />,

            <CoreTypography color='text.secondary'>Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { FamilyContactsBulkInvite } = await import('@features/invitations/FamilyContactsBulkInvite');

          return { Component: FamilyContactsBulkInvite };
        },
        errorElement: <ErrorPage />,
        path: USERS_ROUTES.FAMILY_CONTACTS_BULK_INVITE,
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Users />,

            <CoreTypography color='text.secondary'>Family Contacts Bulk Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { UserInvite } = await import('@features/invitations/UserInvite/UserInvite');

          return { Component: UserInvite };
        },
        errorElement: <ErrorPage />,
        path: USERS_ROUTES.USER_INVITE,
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Users />,

            <CoreTypography color='text.secondary'>User Invite</CoreTypography>,
          ],
        },
      },
      {
        async lazy() {
          const { AddUserInvitation } = await import('@features/invitations/AddUserInvitation');

          return { Component: AddUserInvitation };
        },
        errorElement: <ErrorPage />,
        path: USERS_ROUTES.ADD_USER,
        handle: {
          crumb: () => [
            <CommonBreadcrumbs.Home />,
            <CommonBreadcrumbs.Users />,

            <CoreTypography color='text.secondary'>Add User</CoreTypography>,
          ],
        },
      },
    ],
  },
];
